import { Stack, Typography } from '@mui/material';

import { MagicWand01Icon } from '@/components/icons/MagicWand01Icon';
import { Card } from '@/components/layout/Card/Card';
import { HorizontalCarousel } from '@/components/layout/HorizontalCarousel/HorizontalCarousel';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { PresentationTemplateSelectionModal } from '@/modules/presentationTemplates/presentationTemplateSelectionModal/PresentationTemplateSelectionModal';
import { COLORS } from '@/styles/tokens/colors';
import { FONT_WEIGHTS } from '@/styles/tokens/fonts';

import { useClientOnboardingDeliverables } from './hooks/useClientOnboardingDeliverables';

interface ClientDeliverableOptionsProps {
  householdId: string;
  sx?: Record<string, unknown>;
  direction?: 'row' | 'column';
}

/**
 *
 * @description ClientDeliverableOptions is the component that renders the options for the client deliverables.
 * It is used for Go-tier tenants as a replacement for estate tax-related details.
 */
export function ClientDeliverableOptions({
  householdId,
  sx = {},
  direction = 'row',
}: ClientDeliverableOptionsProps) {
  const canEdit = useCanEditHouseholdData();

  const {
    presentationTemplateModalOpen,
    setPresentationTemplateModalOpen,
    deliverableCards,
  } = useClientOnboardingDeliverables({
    householdId,
  });

  if (!canEdit) {
    return null;
  }

  return (
    <>
      {presentationTemplateModalOpen && (
        <PresentationTemplateSelectionModal
          householdId={householdId}
          onClose={() => setPresentationTemplateModalOpen(false)}
        />
      )}
      <Card variant="filled-outline" sx={{ p: 2, height: '100%', ...sx }}>
        <Stack spacing={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
            spacing={1}
          >
            <MagicWand01Icon color={COLORS.NAVY[300]} />
            <Typography
              variant="title-s"
              sx={{ fontWeight: FONT_WEIGHTS.bold }}
            >
              Get started with your next deliverable
            </Typography>
          </Stack>

          {direction === 'column' ? (
            <Stack direction={direction} spacing={2} height="100%">
              {deliverableCards}
            </Stack>
          ) : (
            <HorizontalCarousel
              items={deliverableCards}
              numVisibleItems={3}
              gap={24}
            />
          )}
        </Stack>
      </Card>
    </>
  );
}
